import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from 'react-router-dom';

import { AIAssistantsData } from '../../TopBar/types';
import { SidebarAIAssistantSwitcher, SidebarLogo, SidebarSettingsButton } from './components';
import { SidebarAgentGroup } from './components/SidebarAgentGroup';
import { SidebarChannelsGroup } from './components/SidebarChannelsGroup';
import { SidebarConversationsGroup } from './components/SidebarConversationsGroup';
import { SidebarPreviewButton } from './components/SidebarPreviewButton';
import { QueryKey } from '@/Config/api/queryKey';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { TierPlanCard } from '@/Domain/components/TierPlanCard/TierPlanCard';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { cn } from '@/Domain/utils/cn';

export function AppSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar();
  // On mobile, always show expanded content since the Sheet is full-width
  const isCollapsed = state === 'collapsed' && !isMobile;

  // The mobile sidebar is an overlay Sheet, so leaving it open after a navigation
  // hides whatever the tap just asked for. Keyed on the history entry, not on parts
  // of the URL: react-router mints a fresh `key` per navigation, so this catches the
  // ones that change only the query string (the inbox filters) or only the hash (the
  // plan card's jump to the billing FAQ) — both of which a `pathname` key misses,
  // silently. No `isMobile` guard: `openMobile` is ignored on desktop and
  // `setOpenMobile` is a stable state setter.
  const { key: locationKey } = useLocation();
  useEffect(() => {
    setOpenMobile(false);
  }, [locationKey, setOpenMobile]);

  const shortcuts = useMemo(() => ['1', '2', '3', '4', '5', '6', '7', '8'], []);

  const { data: aiAssistantsData } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const scenarioIdPermissions = aiAssistantsData?.current_ai_assistant.permissions;

  const { data: subscriptionData } = useGetSubscription();
  const scenarioIdTier: TierEnum | undefined = subscriptionData?.current_plan.tier;

  useHotkeys(
    shortcuts,
    (event) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.repeat) return;

      const activeElement = document.activeElement as HTMLElement | null;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      const target = document.querySelector<HTMLElement>(`[data-shortcut="${event.key}"]`);
      if (!target) return;

      event.preventDefault();
      target.click();
    },
    [shortcuts],
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className={cn('h-[53px] border-b border-sidebar-border')}>
        <SidebarLogo />
      </SidebarHeader>

      <SidebarHeader>
        <SidebarAIAssistantSwitcher
          aiAssistantsData={aiAssistantsData}
          hasSubscriptionAccess={!!scenarioIdPermissions?.subscription}
        />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarPreviewButton />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <SidebarConversationsGroup
          isLocked={!scenarioIdPermissions?.conversations}
          simulationLocked={!scenarioIdPermissions?.simulation}
        />
        <SidebarAgentGroup isLocked={!scenarioIdPermissions?.ai_assistant} />
        <SidebarChannelsGroup isLocked={!scenarioIdPermissions?.ai_assistant} />
      </SidebarContent>

      <SidebarFooter>
        {scenarioIdTier && scenarioIdTier !== TierEnum.CUSTOM && (
          <TierPlanCard open={!isCollapsed} />
        )}

        <SidebarSettingsButton />
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
